import { useState, type CSSProperties } from "react";
import { imageBlockSrc, type ImageContentBlock } from "@/lib/chat";
import { ImageLightbox } from "./ImageLightbox";

const THUMBNAIL_MAX_HEIGHT = 220;

/** 寸法が分かる画像は読み込み前から表示枠を確保し、読み込みで履歴の高さが変わらないようにする。 */
function reservedSizeStyle(image: ImageContentBlock): CSSProperties {
	const { source } = image;
	if (source.type !== "url" || !source.width || !source.height) return {};
	const scale = Math.min(1, THUMBNAIL_MAX_HEIGHT / source.height);
	return { width: source.width * scale, aspectRatio: `${source.width} / ${source.height}` };
}

interface MessageImagesProps {
	images: ImageContentBlock[];
}

/**
 * メッセージ本文に含まれる画像ブロックをサムネイル表示する。画像がなければ何も描画しない。
 * サムネイルをクリックすると原寸表示のライトボックスを開く。
 */
export function MessageImages({ images }: MessageImagesProps) {
	const [openImage, setOpenImage] = useState<ImageContentBlock | null>(null);

	if (images.length === 0) return null;

	return (
		<>
			<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
				{images.map((image, idx) => (
					<button
						key={idx}
						type="button"
						title="クリックで拡大"
						onClick={() => setOpenImage(image)}
						style={{
							border: "none",
							padding: 0,
							margin: 0,
							background: "none",
							cursor: "pointer",
							display: "block",
						}}
					>
						<img
							data-testid="message-image"
							loading="lazy"
							src={imageBlockSrc(image)}
							style={{
								maxHeight: THUMBNAIL_MAX_HEIGHT,
								maxWidth: "100%",
								borderRadius: "var(--radius-4)",
								boxShadow: "var(--line-1) 0 0 0 1px",
								objectFit: "contain",
								display: "block",
								...reservedSizeStyle(image),
							}}
						/>
					</button>
				))}
			</div>
			{openImage && <ImageLightbox image={openImage} onClose={() => setOpenImage(null)} />}
		</>
	);
}
